// computeHistoricalFinancials.js — derive IS / BS / CFS from historical GL lines + mappings
// Input:
//   glLines: historical_gl_lines rows { month, gl_name, gl_category, amount, statement_type, sort_order }
//   mappings: gl_line_mappings rows { gl_name, statement_type, gl_category, is_category, is_line_key, display_name }
//   months: YYYY-MM[] from historical_start to close_month (IS months)
//   priorDecMonth: YYYY-MM — the December preceding historical_start (BS opening balance)
// Output: { is, bs, cfs }

// ★ N1 (Build A): the ONE payroll matcher in the codebase, IMPORTED — never re-derived here.
// Step 3 (buildMappingsFromLines), inferIsMeta, and this bridge all key on the same matcher, so
// they cannot drift about "Salaries" the way matchISLineKey vs matchPayrollRole once did.
import { matchPayrollRole, resolvePayrollDept } from './inferIsMeta';
// ★ Build 1 (PART 1a): the ONE IS-category matcher, the same one perDgPayrollActuals.js:71 uses to
// derive the cat_slug for the per-department payroll WRITE path. Importing it here means the review
// screen buckets a recovered payroll row into exactly the section its history is written under —
// not a second, drifting keyword rule.
import { matchISCategory } from './isCategoryMatch';
import { REV_KEYWORDS_RE, isComputedRow, isExcludedComputedRow, buildRevKeyResolver } from './revKeys';   // W-082: one owner
// ★ Build 1 (PART 1b): the SAME slug→label helper the model's own IS builder uses for its per-
// department sub-headers (buildISLines.js:370), so a department is named identically on the review
// screen and in the model. Used only to disambiguate rows that would otherwise render as two
// identical "Salaries" lines.
import { displayGroupLabel, IS_CATEGORY_DISPLAY_TO_SLUG } from '../engines/lineRoles';
// ★ Build C (D3b): the model's OWN IS assembler (exported by Build B, c851052) and the ONE
// payroll-key builder. Called, never edited — the review screen's IS order, labels, SG&A
// wrapper and per-department sub-headers/sub-totals are now the model's.
import { assembleISSkeleton } from '../engines/buildISLines';
import { buildPayrollIsLineKey } from '../engines/payrollKeys';
import { groupIdOf, templateGroupLabels } from './seatDepartments';
// ★ B2 — the `generateLabel as cfsLabel` import is GONE with the dead code that used it. It
// existed so this file's own CFS rows were WORDED like the model's; the review screen now
// renders the model's rows directly (buildCFSLinesForCompany), which carry the model's labels
// already. The coupling is not lost — it is now structural rather than mirrored.

const r2 = n => Math.round(n * 100) / 100;

// matchISCategory's display form → this module's bucket name. Only the four PAYROLL-BEARING
// sections are listed on purpose: 'Other' / 'Skip' / '' are NOT payroll destinations (the `da` and
// `oie` buckets are for D&A and non-operating items), so anything else falls through to the
// fail-soft `payroll` bucket below.
const PAYROLL_SECTION_OF = { 'COGS': 'cogs', 'S&M': 'sm', 'G&A': 'ga', 'R&D': 'rd' };

// matchPayrollRole's role -> the paytype the leaf key uses. Commissions FOLD into bonuses for the
// per-department path, exactly as perDgPayrollActuals.js:27 does — one rule, not two.
const ROLE_TO_PAYTYPE = {
    payroll_wages: 'wages', payroll_bonuses: 'bonuses', payroll_commissions: 'bonuses',
    payroll_tax: 'payroll_taxes', payroll_benefits: 'benefits',
};

// ★ THE PRIVATE COPY IS GONE. This file used to carry its own `slugForRevenueKey` — the
// same rule buildFinancialsPayload uses for its rev_<slug> fallback key — with a comment
// asserting the equivalence it had no way to enforce. Both now call the ONE export in
// utils/bsKey.js, so a Skip'd revenue row provably lands on the id its history is stored
// under in is_actuals rather than on an id that merely happened to match. (§6; the merge
// was proven a no-op over 919 real names before it was made — diffs/phase2-b7-verify.txt.)

// ★ B2 — the four CLS_* classification shapes stood here and are DELETED with the parallel CFS
// they fed. They mirrored buildCFSLines.classifyForCFS so this file's rows matched the model's;
// the model's own rows are rendered now, so the mirror has no reader.

/* W-082 (2026-09-30): REV_KEYWORDS, COMPUTED_EXACT, isComputedRow, isExcludedComputedRow and REV_KEYWORDS_RE MOVED to
 * utils/revKeys.js -- the revenue-line rules, one owner -- and are re-exported here, so every existing import is unchanged. */
export { isComputedRow, isExcludedComputedRow, REV_KEYWORDS_RE };

function isLikelyRevenue(gl) {
    if (isComputedRow(gl)) return false;
    return REV_KEYWORDS_RE.test(gl.gl_name || '') || REV_KEYWORDS_RE.test(gl.gl_category || '');
}

function emptyAmounts(months) {
    const a = {};
    for (const m of months) a[m] = 0;
    return a;
}

function sumAmounts(lines, months) {
    const out = emptyAmounts(months);
    for (const m of months) {
        let s = 0;
        for (const l of lines) s = r2(s + (l.amounts[m] || 0));
        out[m] = r2(s);
    }
    return out;
}

function addMaps(a, b, months) {
    const out = emptyAmounts(months);
    for (const m of months) out[m] = r2((a[m] || 0) + (b[m] || 0));
    return out;
}

function subMaps(a, b, months) {
    const out = emptyAmounts(months);
    for (const m of months) out[m] = r2((a[m] || 0) - (b[m] || 0));
    return out;
}

// Group GL rows into per-line objects keyed by (statement_type|gl_name|gl_category|display_group)
//
// ★ Build 1 (PART 1b) — display_group IS PART OF THE ROW IDENTITY. The canonical template puts the
// SECTION in is_category → gl_category and the DEPARTMENT in its own display_group column, so
// grouping on (gl_name, gl_category) alone re-merged the four S&M departments into ONE "Salaries"
// row (measured on cid=8: 11 rows lost that way). This is the same widening the import's own row
// identity got in a6cd0f8, and the same key perDgPayrollActuals.js:49 already groups on — the
// display side was simply never brought along. Legacy tenants carry '' and are unaffected
// (measured: cid=3's displayed IS rows 75 → 75).
//
// ★ NOTE the deliberate asymmetry: attachMapping() below still looks a row up in gl_line_mappings
// by (statement_type|gl_name|gl_category) WITHOUT display_group — glm has no per-department rows,
// so the mapping is shared by every department of a line and that lookup must stay narrow.
function groupLines(glLines, months, allMonthsIncludingPrior) {
    const groups = new Map();
    for (const row of glLines) {
        const key = `${row.statement_type}|${row.gl_name}|${row.gl_category || ''}|${row.display_group || ''}`;
        if (!groups.has(key)) {
            groups.set(key, {
                statement_type: row.statement_type,
                gl_name: row.gl_name,
                gl_category: row.gl_category || '',
                display_group: row.display_group || '',
                sort_order: row.sort_order == null ? 999999 : row.sort_order,
                amounts: emptyAmounts(allMonthsIncludingPrior),
            });
        }
        const g = groups.get(key);
        if (row.month in g.amounts) {
            g.amounts[row.month] = r2((g.amounts[row.month] || 0) + (parseFloat(row.amount) || 0));
        }
    }
    // Sort by sort_order within statement type
    const arr = Array.from(groups.values());
    arr.sort((a, b) => {
        if (a.statement_type !== b.statement_type) return a.statement_type < b.statement_type ? -1 : 1;
        return a.sort_order - b.sort_order;
    });
    return arr;
}

// Attach mapping info (is_category, is_line_key, display_name) to a grouped line
function attachMapping(line, mappingLookup) {
    const key = `${line.statement_type}|${line.gl_name}|${line.gl_category || ''}`;
    const m = mappingLookup.get(key);
    return {
        ...line,
        is_category: m?.is_category || null,
        is_line_key: m?.is_line_key || null,
        /* ★ THE PINNED LEAF KEY (Build 1). The bridge is a SECOND implementation of the
         * historical IS, so if it kept re-slugging a renamed line's name it would report
         * values under an id the model does not have — and it IS reachable after a rename,
         * because "Edit Onboarding" opens the wizard for a live tenant with no gate
         * (measured, diffs/rekey-phase0b.txt §0). Carrying the column costs nothing: the
         * mappings are already this function's input. GATE-BRIDGE covers it. */
        leaf_key: m?.leaf_key || null,
        display_name: m?.display_name || line.gl_name,
        // ★ THE MAPPING'S department, kept SEPARATE from the row's own (`line.display_group`, which
        // the spread above preserves). Both are real and they live in different columns on different
        // tenants: cid=3 carries "sales_mktg" on its gl_line_mappings rows (Session 4f), cid=8
        // carries it on its historical_gl_lines rows (the canonical template's own column). Not
        // carrying this was why six S&M vendor rows reached the model's assembler with no department
        // and orphaned — on BOTH tenants, including the one whose live IS page nests them correctly.
        mapping_display_group: m?.display_group ?? null,
    };
}

// ── IS computation ────────────────────────────────────────────
// Aligned with main finmodel computeIS.js structure:
//   Revenue → Total Revenue
//   COGS → Total COGS → Gross Profit → GP Margin
//   SG&A: S&M, G&A, R&D, D&A sub-sections → Total SG&A
//   Operating Income → EBIT Margin
//   Other Income/Expenses → Total OIE → Net Income → EBITDA
// OIE convention for historical data (QB/credit-style raw GL):
//   Income-type lines (Interest Income, Dividend Income, …) are stored as negative credits in
//   the source GL. We flip their sign at the line level so they display as positive, then:
//     totalOIE = sum(income_positive) − sum(expense);  netIncome = operatingIncome + totalOIE
// ★ N3 (Build A): detect income by the gl_name ending in "Income" (mirrors
// inferIsMeta.matchOtherRole's /income\s*$/ rule), NOT a hardcoded is_line_key set. The old
// {other_income, interest_income, dividend_income} set never matched the oi_* slugs onboarding
// generates (oi_interest_income, oi_dividend_income, oi_grant_reward_income …), so every OIE
// income line displayed with the WRONG SIGN. This is DISPLAY-ONLY: for a negative-credit amount
// R, the "income" path flips it to −R then adds (+(−R)); the "expense" path keeps R then
// subtracts (−R); both contribute +|income| to totalOIE — so Net Income is IDENTICAL either way.
const isOieIncome = (l) => /income\s*$/i.test(l.gl_name || '');

function computeIS(groupedLines, months, deptMappings, resolveRevKey) {
    const buckets = {
        revenue: [],
        cogs: [],
        sm: [],
        ga: [],
        rd: [],
        da: [],     // D&A from "Other" category (operating) — routed by gl_name (N2)
        oie: [],    // Non-operating: income tax, interest, other inc/exp
        payroll: [], // ★ N1: Skip'd payroll recovered here (real expense, engine-sourced in the model)
        skip: [],
    };
    const catMap = {
        'COGS': 'cogs',
        'S&M': 'sm',
        'G&A': 'ga',
        'R&D': 'rd',
    };

    for (const line of groupedLines) {
        if (line.statement_type !== 'IS') continue;
        if (isExcludedComputedRow(line, line.is_category)) continue;   // W-068 (was isComputedRow(line))
        const cat = line.is_category;
        const normCat = (cat || '').toLowerCase();
        if (cat && catMap[cat]) {
            buckets[catMap[cat]].push(line);
        } else if (cat === 'Other') {
            // ★ N2 (Build A): route D&A by gl_name keyword, NOT the DEAD is_line_key==='da_expense'.
            // B3-8 (bcfc000) deleted matchOtherLineKey — the generator of 'da_expense' — so
            // onboarding now emits oi_depreciation_expense / oi_amortization_expense and the old
            // check never fired (D&A fell to OIE, understating operating D&A + EBITDA + the CFS
            // add-back; NI-neutral). gl_name keyword mirrors inferIsMeta.matchOtherRole and cannot
            // drift when a slug changes.
            if (/depreciation|amortization/i.test(line.gl_name || '')) buckets.da.push(line);
            else buckets.oie.push(line);
        } else if (!cat || normCat === 'skip') {
            // ★ N1 (Build A) — PAYROLL FIRST. The ORDER is the root cause. A Skip'd payroll row
            // carries a gl_category like "Sales and Marketing" / "Revenue Operations" that matches
            // REV_KEYWORDS, so tested SECOND it lands in REVENUE and is added to NI as income
            // (measured on cid=3: $8.76M of payroll booked as revenue). matchPayrollRole (the ONE
            // shared matcher) is checked before isLikelyRevenue — mirroring inferIsMeta's own rule
            // (payroll before is_category).
            // ★ WHY a Skip'd line has real dollars: 'Skip' means "no leaf in the MODEL's chart of
            // accounts" (the model sources payroll from the payroll engine, revenue from its module
            // engines), NOT "this money was not spent". This bridge recomputes the WORKBOOK's
            // history, where payroll is simply an operating expense.
            //
            // ★ Build 1 (PART 1a) — SECTION the recovered payroll instead of lumping it. Build A's
            // comment at the 'Personnel' bucket refused to "split by gl_category KEYWORD (that would
            // be a heuristic)". That objection is now obsolete: perDgPayrollActuals.js:71 performs
            // exactly this split, with matchISCategory, to derive the is_actuals keys this same
            // payroll is WRITTEN under. Reusing that matcher here is the one-matcher rule, not a new
            // guess — the screen and the stored history agree by construction.
            // The four payroll sections only; anything else (no match, 'Other', 'Skip') FAILS SOFT
            // into the 'Personnel' bucket, which still renders AND still folds into Total SG&A — a
            // visible row must count (B3-13). Measured: 0 fail-soft rows on cid=3 and cid=8.
            if (matchPayrollRole(line.gl_name)) {
                const sec = PAYROLL_SECTION_OF[matchISCategory(line.gl_category)];
                if (sec) buckets[sec].push(line);
                else buckets.payroll.push(line);
            }
            else if (isLikelyRevenue(line)) buckets.revenue.push(line);
            else buckets.skip.push(line);
        } else {
            buckets.skip.push(line);
        }
    }

    // Normalize OIE income-type lines: flip sign so they display as positive income.
    // Raw GL has income stored as negative credits; we invert so the user sees e.g.
    // "Interest Income: 26.78" instead of "(26.78)".
    buckets.oie = buckets.oie.map(l => {
        if (!isOieIncome(l)) return l;
        const flipped = {};
        for (const m of Object.keys(l.amounts || {})) flipped[m] = r2(-(l.amounts[m] || 0));
        return { ...l, amounts: flipped };
    });

    // ★ Build C (D3b) — THE BRIDGE'S PARALLEL IS SECTION LIST IS DELETED.
    // buildSection() and the nine named section objects (Revenue / Cost of Goods Sold / … /
    // "Personnel" / "Unmapped / Skip") were this screen's own copy of the statement's structure and
    // labels. Structure now comes from assembleISSkeleton and labels from the model's own
    // alwaysPresent templates, so the copy is gone — including the two bridge-only sections, which
    // D5's named diagnostic replaces. The BUCKETS below stay: they are the value computation, which
    // is the bridge's actual job.

    const totalRevenue = sumAmounts(buckets.revenue, months);
    const totalCOGS = sumAmounts(buckets.cogs, months);
    const grossProfit = subMaps(totalRevenue, totalCOGS, months);

    const totalSM = sumAmounts(buckets.sm, months);
    const totalGA = sumAmounts(buckets.ga, months);
    const totalRD = sumAmounts(buckets.rd, months);
    const totalDA = sumAmounts(buckets.da, months);
    const totalPayroll = sumAmounts(buckets.payroll, months);   // ★ N1: recovered Skip'd payroll

    const totalSGA = emptyAmounts(months);
    for (const m of months) {
        // ★ N1: fold recovered payroll into Total SG&A so it SUBTRACTS at Operating Income — the
        // one line that takes cid=3's Step-4b balance check from −$12.67M to $0.00 (all 27 months).
        totalSGA[m] = r2((totalSM[m] || 0) + (totalGA[m] || 0) + (totalRD[m] || 0) + (totalDA[m] || 0) + (totalPayroll[m] || 0));
    }

    const operatingIncome = emptyAmounts(months);
    for (const m of months) {
        operatingIncome[m] = r2((grossProfit[m] || 0) - (totalSGA[m] || 0));
    }

    // totalOIE = Net Other Income/(Expense): positive contributes to NI, negative reduces.
    // Income-type lines (already sign-flipped above) add positively; expense-type subtract.
    const totalOIE = emptyAmounts(months);
    for (const m of months) {
        let net = 0;
        for (const l of buckets.oie) {
            const v = l.amounts[m] || 0;
            net = r2(net + (isOieIncome(l) ? v : -v));
        }
        totalOIE[m] = r2(net);
    }

    const netIncome = emptyAmounts(months);
    for (const m of months) {
        netIncome[m] = r2((operatingIncome[m] || 0) + (totalOIE[m] || 0));
    }

    const ebitda = emptyAmounts(months);
    for (const m of months) {
        ebitda[m] = r2((operatingIncome[m] || 0) + (totalDA[m] || 0));
    }

    const gpMargin = {};
    const ebitMargin = {};
    for (const m of months) {
        const rev = totalRevenue[m] || 0;
        gpMargin[m] = rev === 0 ? null : r2(((grossProfit[m] || 0) / rev) * 100);
        ebitMargin[m] = rev === 0 ? null : r2(((operatingIncome[m] || 0) / rev) * 100);
    }

    // ★ N4 (Build A) — FAIL-LOUD guard, NOT a guess. This recompute assumes the workbook stores
    // revenue as positive debits (it sums the revenue bucket as-is). A tenant whose workbook stores
    // revenue as NEGATIVE credits would produce a negative Total Revenue → wrong Gross Profit / NI /
    // balance check. That is un-fixable without the tenant's own sign convention, so instead of
    // silently producing a wrong NI we surface a NAMED warning on the review screen. (cid=3:
    // revenueSum > 0 → null → no change.)
    const revenueSum = months.reduce((s, m) => s + (totalRevenue[m] || 0), 0);
    const revenueSignWarning = revenueSum < 0
        ? `Recomputed Total Revenue over the historical window is negative (${r2(revenueSum)}). The source `
          + `workbook likely stores revenue with the opposite sign to what this recompute assumes `
          + `(positive debits). Net Income and the balance check may be wrong — verify the income sign `
          + `convention before relying on these statements.`
        : null;

    // ★ Build C (D3b) — THE BRIDGE'S OWN LEAVES, KEYED BY THE MODEL'S LEAF IDS.
    //
    // The Step-4 review screen cannot use the model's LEAF SET: buildISLeaves derives payroll leaves
    // from department_mappings (absent until Step 5c) and revenue leaves from
    // revenue_module_mappings (absent until Step 7), and a "Handled by Payroll" row carries role
    // NULL by design so it produces no glLeaf at all. But it does not need to — it needs the model's
    // LAYOUT. So the bridge builds leaves of its own, under the SAME ids the model would use, and
    // hands them to assembleISSkeleton. Every id here comes from a shared derivation, never a local
    // rule: matchISCategory for the section, the row's own display_group (or resolvePayrollDept's
    // token-match, exactly as perDgPayrollActuals does) for the department, and
    // buildPayrollIsLineKey for the key itself — the same key the historical payroll is WRITTEN
    // under in is_actuals.
    //
    // ★ D5 — NO INVENTED SECTION. A row whose id or subsection cannot be formed is neither dropped
    // (B3-13: a visible row must count) nor given a bridge-only "Personnel"/"Unmapped" home. It goes
    // to `unplaced`, which the page surfaces as a named diagnostic outside the statement.
    const depts = deptMappings || [];
    const BUCKET_TO_SUBSECTION = { revenue: 'revenue', cogs: 'cogs', sm: 'sm', ga: 'ga', rd: 'rd', da: 'ooe', oie: 'oie' };
    const isLeavesBySub = {};
    const isValuesById = {};
    const isUnplaced = [];
    const addLeafValue = (id, l) => {
        const dest = isValuesById[id] || (isValuesById[id] = {});
        for (const m of months) dest[m] = r2((dest[m] || 0) + (l.amounts[m] || 0));
    };
    let leafSort = 0;
    const groupTexts = [];   // 2026-10-28: the template's own group texts, for the headings of groups no department owns
    for (const bucketKey of Object.keys(BUCKET_TO_SUBSECTION)) {
        const subsection = BUCKET_TO_SUBSECTION[bucketKey];
        for (const l of buckets[bucketKey]) {
            let id = null, dg = null;
            if (l.is_line_key) {
                id = l.is_line_key;                       // a mapped row: the model uses this id verbatim
                // ★ A MAPPED ROW HAS A DEPARTMENT TOO. The payroll branch below has always read one;
                // this branch never did, so every mapped row — including the S&M vendor lines —
                // reached assembleISSkeleton with no department and was orphaned out of its own
                // sub-group. gl_line_mappings FIRST (it is what the model itself reads, so the review
                // screen and the live IS page agree), then the row's own historical_gl_lines value
                // (where the canonical template puts it). A row tagged in NEITHER place stays null
                // and still orphans + warns — that behaviour is correct and is left alone.
                // 2026-10-28: a template group TEXT is the group deptSlug(text) (utils/seatDepartments groupIdOf)
                const dgText = l.mapping_display_group || l.display_group;
                if (dgText) groupTexts.push(dgText);
                dg = groupIdOf(dgText) || null;
            } else {
                const role = matchPayrollRole(l.gl_name);
                if (role) {
                    const catSlug = IS_CATEGORY_DISPLAY_TO_SLUG[matchISCategory(l.gl_category)];
                    if (catSlug) {
                        const res = resolvePayrollDept(l.gl_category, depts);
                        if (l.display_group) groupTexts.push(l.display_group);
                        dg = groupIdOf(l.display_group) || res.display_group || null;   // 2026-10-28: the ONE rule
                        id = buildPayrollIsLineKey(catSlug, dg || catSlug, ROLE_TO_PAYTYPE[role]);
                    }
                } else if (subsection === 'revenue') {
                    /* ★★ THE DEDUPE COUNTER, PORTED FROM THE WRITE PATH. buildFinancialsPayload
                     * numbers same-slugging revenue lines rev_x, rev_x_2, … ; this loop did not,
                     * so two revenue rows whose names normalise alike COLLAPSED onto one id.
                     * ★ WHAT THAT ACTUALLY COST — MEASURED on a cid=40 probe, not reasoned:
                     * addLeafValue ADDS into the id and the leaf push is `some(x => x.id === id)`
                     * guarded, so the dollars were CONSERVED (49,684.10 either way) and net income
                     * did NOT move — it is summed from `buckets` above, before this walk. What
                     * moved was the ROW SET: 1 rendered row where the operator has 2, the second
                     * line's $49,705.00 reported under the FIRST line's label, and one id where
                     * is_actuals holds two. ⇒ a DISPLAY + KEY defect, not an arithmetic one, and
                     * the more deceptive for it: the review screen still adds up.
                     * ⓘ LATENT, AND MEASURED SO: across cid 3/8/36/40 the 52/47/47/47 distinct
                     * IS gl_names produce exactly as many distinct slugs — ZERO collisions — and
                     * no stored is_actuals key ends in _<n>. So this counter changes no id on any
                     * tenant today. It is here because the WRITE path has always had one and this
                     * READ path did not: two sides of one key rule that were structurally free to
                     * disagree the first time a workbook names two revenue lines alike. Same
                     * base-guard and same counter semantics as the write path, so they cannot. */
                    /* W-082 (2026-09-30): THE KEY COMES FROM THE ONE RESOLVER (utils/revKeys.js buildRevKeyResolver) -- the same
                     * rule the Step-4 import books under and the Step-3 save pins with (the pinned key wins; else rev_<name> with
                     * the `_${n}` dedupe, reserving every key another line holds). This loop carried its own copy of the counter
                     * above, keyed per (line, department) group and without the reservation. */
                    id = resolveRevKey(l.gl_name, l.gl_category);
                }
            }
            if (!id) { isUnplaced.push({ gl_name: l.gl_name, gl_category: l.gl_category, amounts: l.amounts }); continue; }
            addLeafValue(id, l);
            const bag = isLeavesBySub[subsection] || (isLeavesBySub[subsection] = []);
            if (!bag.some(x => x.id === id)) {
                bag.push({
                    id,
                    label: l.display_name || l.gl_name,
                    level: undefined,
                    source: 'historical',
                    role: l.role || undefined,
                    subsection,
                    sort_order: leafSort++,
                    display_group: dg,
                });
            }
        }
    }
    // Anything the bridge recovered into the fail-soft payroll bucket has no section at all.
    for (const l of buckets.payroll) isUnplaced.push({ gl_name: l.gl_name, gl_category: l.gl_category, amounts: l.amounts });
    for (const l of buckets.skip) isUnplaced.push({ gl_name: l.gl_name, gl_category: l.gl_category, amounts: l.amounts });
    // Department header labels, straight from the operator's own Step-5c block names. Absent →
    // assembleISSkeleton falls back to displayGroupLabel(slug), the model's own fallback.
    const isDgLabels = templateGroupLabels(groupTexts, depts);   // 2026-10-28: {} when every group is a slug
    for (const d of depts) if (d.display_group && d.display_group_label) isDgLabels[d.display_group] = d.display_group_label;

    return {
        months,
        isLeavesBySub, isValuesById, isDgLabels, isUnplaced,
        totalRevenue,
        totalCOGS,
        grossProfit,
        totalSM,
        totalGA,
        totalRD,
        totalDA,
        totalPayroll,
        totalSGA,
        operatingIncome,
        totalOIE,
        netIncome,
        ebitda,
        gpMargin,
        ebitMargin,
        revenueSignWarning,
    };
}

// ── BS computation ────────────────────────────────────────────
// netIncomeByMonth: { [YYYY-MM]: amount } from IS; injected as a synthetic
// "Retained Earnings (from P&L)" line under equity so Total Equity balances.
// cashRollup (★ Build 1, PART 2): an OPTIONAL operator-defined sub-rollup inside Current Assets —
//   { label: string, memberGlNames: string[] }
// The model shows a "Total Cash" line because buildBSLines rolls the bs_actuals_meta leaves whose
// subcategory is 'clearing' into total_clearing; this screen had no sub-section subtotal at all, for
// any tenant. The caller owns BOTH halves of the definition — which leaves are members and what the
// row is called — because both are the operator's own Step-4a answers (never a literal here, and
// never a keyword guess — B2 deleted the CFS-internal cash keyword heuristic that used to sit
// below, and it was deliberately never reused for this anyway). Members are matched by
// gl_name/display_name, the same way isSweep does it.
// ★ Build A (D1) — THE SYNTHETIC RETAINED-EARNINGS PLUG HAS A REAL HOME IN THE MODEL.
//
// The first cut of this build treated the plug as a bridge-only extra with no model counterpart and
// folded it into Total Equity by hand. That was wrong, and the gate caught it: the model's BS DOES
// carry an equity leaf `net_income` (role 'ytd_net_income', subcategory 'retained_earnings') and it
// is already a member of `total_equity.sumOf`. The bridge drops the workbook's own "Net Income" BS
// row (isComputedRow excludes it) and substitutes this plug — so the plug IS that row's value.
// Mapping it onto `net_income` makes Total Equity fall out of the model's own sumOf with no
// special-casing, and removes the "extra row" entirely.
export const RE_PLUG_TARGET = 'net_income';
// The model's own balance-check row (type 'check'); the bridge supplies its checkRow for it.
export const CHECK_ROW_ID = 'check';

// The four CFS rows evaluateCFSSkeleton derives itself, after every other row is valued (they
// depend on the section totals). Listed so the per-row loop does not report them as unvalued.
const CASH_FORMULA_IDS = new Set(['cash_avail', 'beg_cash', 'chg_cash', 'end_cash']);

function computeBS(groupedLines, bsMonths, netIncomeByMonth, cashRollup, bsKeyOf) {
    const buckets = {
        current_assets: [],
        fixed_assets: [],
        intangible_assets: [],
        current_liabilities: [],
        lt_liabilities: [],
        equity: [],
        skip: [],
    };
    const catMap = {
        'Current Assets': 'current_assets',
        'Fixed Assets': 'fixed_assets',
        'Intangible Assets': 'intangible_assets',
        'Current Liabilities': 'current_liabilities',
        'LT Liabilities': 'lt_liabilities',
        'Equity': 'equity',
    };

    for (const line of groupedLines) {
        if (line.statement_type !== 'BS') continue;
        if (isExcludedComputedRow(line, line.is_category)) continue;   // W-068 (was isComputedRow(line))
        const cat = line.is_category;
        if (cat && catMap[cat]) buckets[catMap[cat]].push(line);
        else buckets.skip.push(line);
    }

    // Inject synthetic Retained Earnings line.
    //  • priorDecMonth: plug value = totalAssets − totalLiabilities − equityGL total
    //    (makes the opening BS balance exactly, absorbing any historical RE)
    //  • subsequent months: YTD cumulative Net Income, reset each January
    if (netIncomeByMonth) {
        const reAmounts = emptyAmounts(bsMonths);

        // Detect priorDecMonth: the first bsMonth not present in netIncomeByMonth.
        const priorDec = bsMonths.find(m => !(m in netIncomeByMonth)) || null;

        // Compute opening plug for priorDec using raw equity GL lines (before the
        // synthetic line is pushed) + asset/liability totals.
        if (priorDec) {
            const sumAt = (bucket, m) => {
                let s = 0;
                for (const l of bucket) s = r2(s + (l.amounts[m] || 0));
                return r2(s);
            };
            const assets = r2(
                sumAt(buckets.current_assets, priorDec) +
                sumAt(buckets.fixed_assets, priorDec) +
                sumAt(buckets.intangible_assets, priorDec)
            );
            const liabs = r2(
                sumAt(buckets.current_liabilities, priorDec) +
                sumAt(buckets.lt_liabilities, priorDec)
            );
            const equityGL = sumAt(buckets.equity, priorDec);
            reAmounts[priorDec] = r2(assets - liabs - equityGL);
        }

        // YTD cumulative Net Income for all other months, reset at January.
        let ytd = 0;
        let prevYear = null;
        for (const m of bsMonths) {
            if (m === priorDec) continue;
            if (!(m in netIncomeByMonth)) { reAmounts[m] = 0; continue; }
            const [year, mo] = m.split('-');
            if (mo === '01' || year !== prevYear) {
                ytd = r2(netIncomeByMonth[m] || 0);
            } else {
                ytd = r2(ytd + (netIncomeByMonth[m] || 0));
            }
            reAmounts[m] = ytd;
            prevYear = year;
        }

        buckets.equity.push({
            statement_type: 'BS',
            gl_name: '__retained_earnings_from_pl__',
            gl_category: '',
            display_name: 'Retained Earnings (from P&L)',
            is_category: 'Equity',
            is_line_key: null,
            amounts: reAmounts,
            _synthetic: true,
        });
    }

    // ★ Build A (D1) — THE BRIDGE'S PARALLEL BS SECTION LIST IS DELETED.
    // `sectionDefs` (a hand-kept copy of the model's six BS sections) and the `sections` array it
    // produced, plus Build 1's cashRollup sub-total attach, existed only to drive a render that now
    // walks the MODEL's skeleton instead. `total_clearing` is the model's own row, emitted at its
    // own position from bs_actuals_meta — the review screen no longer decides where Total Cash
    // goes, which sections exist, or what they are called.

    const totalCurrentAssets = sumAmounts(buckets.current_assets, bsMonths);
    const totalFixedAssets = sumAmounts(buckets.fixed_assets, bsMonths);
    const totalIntangibleAssets = sumAmounts(buckets.intangible_assets, bsMonths);
    const totalAssets = emptyAmounts(bsMonths);
    for (const m of bsMonths) {
        totalAssets[m] = r2((totalCurrentAssets[m] || 0) + (totalFixedAssets[m] || 0) + (totalIntangibleAssets[m] || 0));
    }
    const totalCurrentLiabilities = sumAmounts(buckets.current_liabilities, bsMonths);
    const totalLTLiabilities = sumAmounts(buckets.lt_liabilities, bsMonths);
    const totalLiabilities = addMaps(totalCurrentLiabilities, totalLTLiabilities, bsMonths);
    const totalEquity = sumAmounts(buckets.equity, bsMonths);
    const totalLiabilitiesAndEquity = addMaps(totalLiabilities, totalEquity, bsMonths);
    const checkRow = subMaps(totalAssets, totalLiabilitiesAndEquity, bsMonths);

    // ★ Build A (D1) — VALUES KEYED BY THE MODEL'S LEAF ID. This is the whole contract between the
    // bridge and the model's skeleton: the bridge owns the numbers, the model owns the layout, and
    // they meet on the leaf id. `bsKeyOf` is supplied by the caller (AdminPage), which owns the ONE
    // key derivation that also produced bs_actuals — the bridge does not re-derive it.
    const bsValuesByKey = {};
    const bsUnkeyed = [];
    if (bsKeyOf) {
        for (const cat of Object.keys(buckets)) {
            for (const l of buckets[cat]) {
                const key = l._synthetic ? RE_PLUG_TARGET : bsKeyOf(l);
                if (!key) { bsUnkeyed.push({ gl_name: l.gl_name, gl_category: l.gl_category, category: cat }); continue; }
                const dest = bsValuesByKey[key] || (bsValuesByKey[key] = {});
                for (const m of bsMonths) dest[m] = r2((dest[m] || 0) + (l.amounts[m] || 0));
            }
        }
    }

    return {
        months: bsMonths,
        bsValuesByKey,
        bsUnkeyed,
        totalCurrentAssets,
        totalFixedAssets,
        totalIntangibleAssets,
        totalAssets,
        totalCurrentLiabilities,
        totalLTLiabilities,
        totalLiabilities,
        totalEquity,
        totalLiabilitiesAndEquity,
        checkRow,
        _buckets: buckets,
    };
}

// ── ★ B2 — THE SWEEP DERIVATION IS ALL THAT SURVIVES HERE ──────────────────────
//
// This function used to build a WHOLE PARALLEL CASH-FLOW STATEMENT: its own operating /
// investing / financing line arrays, its own inclusion rules, its own row labels, its own
// section totals and its own beginning/ending-cash roll-forward. Build A moved the review
// screen onto the MODEL's builders (buildCFSLinesForCompany + evaluateCFSSkeleton), and
// from that moment the whole apparatus was computed on every render and thrown away —
// AdminPage reads exactly three things from this function: `months`, `mmSweepAmounts` and
// `sweepWarning`. It is now deleted (§6 escape hatch: "DELETE the parallel rules" — a
// dormant copy is the seed the next divergence grows from).
//
// DELETED WITH IT, because their only callers were inside it: the ACCUM_RE / RE_NI_RE /
// CASH_KW_RE regexes and their isAccumulatedItem / isRetainedEarningsItem /
// isCashClearingLine predicates (double-count exclusions the MODEL now owns in
// buildCFSLines.classifyForCFS + CFS_EXCLUDED_ROLES), the four CLS_* classification shapes,
// and the `generateLabel as cfsLabel` import.
//
// sweepGlName: gl_name of the money-market/sweep account chosen during onboarding. The
// sweep amount is a BS delta — the month-over-month change in that account's own balance —
// and never depended on any of the deleted totals, which is why removing them cannot move
// it (proven: the three kept outputs are byte-identical pre/post on cid=3 and cid=8).
function computeCFS(isData, bsData, months, priorDecMonth, sweepGlName) {
    const cfsMonths = months; // excludes priorDec
    const prevMonth = (m) => {
        const idx = months.indexOf(m);
        if (idx === 0) return priorDecMonth;
        if (idx > 0) return months[idx - 1];
        return priorDecMonth;
    };

    const isSweep = l => !!sweepGlName && (l.gl_name === sweepGlName || l.display_name === sweepGlName);

    // ── Money Market Sweep line (goes inside Financing, after the subtotal) ──
    // Sign: negative when the sweep balance increases (cash swept to MM = cash out),
    //       positive when the sweep balance decreases (drawdown = cash in).
    const sweepBsLines = bsData._buckets.current_assets.filter(isSweep);
    const sweepBalAt = (m) => {
        let s = 0;
        for (const l of sweepBsLines) s = r2(s + (l.amounts[m] || 0));
        return r2(s);
    };
    const mmSweepAmounts = emptyAmounts(cfsMonths);
    for (const m of cfsMonths) {
        const prev = prevMonth(m);
        mmSweepAmounts[m] = r2(-(sweepBalAt(m) - (prev ? sweepBalAt(prev) : 0)));
    }

    // ★ Build 1 (PART 3b) — FAIL LOUD when no sweep account was identified. With sweepGlName null,
    // isSweep() never fires, so no account is recognised as the sweep target and
    // the sweep row is 0.00 in every month — under headings that promise the opposite ("Beginning
    // Cash (less Money Market Account)", "Money Market (Replenishment) / Drawdown"). That silently
    // REDEFINES cash rather than reporting it. Mirrors revenueSignWarning: a named warning the page
    // renders, never a quiet substitution. (cid=3/cid=8 with the sweep resolved → null → no change.)
    const sweepWarning = !sweepGlName
        ? 'No money-market / sweep account is identified, so Beginning and Ending Cash below include '
          + 'EVERY cash account (the money market is not separated out) and the Money Market row is '
          + 'zero in every month. Pick the sweep account on the previous screen to see the intended '
          + 'cash figures.'
        : null;

    // ★ B2 — EXACTLY the three fields AdminPage reads (measured: cfs.months at :3693/:3951,
    // cfs.mmSweepAmounts at :3947, cfs.sweepWarning at :4022/:4025 — and nothing else, anywhere).
    // The eleven that used to sit here (operatingLines, investingLines, financingLines,
    // totalOperating, totalInvesting, totalFinancingBeforeSweep, cashAvailBeforeSweep,
    // totalFinancing, netChangeCash, beginningCash, endingCash) had no reader once Build A put
    // the review screen on the model's own skeleton.
    return {
        months: cfsMonths,
        sweepWarning,
        mmSweepAmounts,
    };
}

// ── ★ Build A — SKELETON EVALUATORS (D1/D2) ───────────────────
//
// The model's builders (buildBSLines / buildCFSLines) emit an ordered skeleton and NOT ONE NUMBER;
// every row carries its own descriptor — `sumOf` for a total, `bsKey`+`sign` for a balance-sheet
// delta, `gross_keys` for the fixed-asset write-off, `isKey` for an income-statement reference.
// These two functions VALUE that skeleton from the bridge's Step-4 primitives. They add NO layout
// knowledge: order, labels, nesting, which totals exist and what each total sums are all read off
// the rows the model produced. The bridge therefore no longer owns a parallel section list.
//
// Both report, rather than hide, a row they cannot value (`missing`) and a bridge value with no
// row to land on (`orphans`) — silently blanking either is how a statement lies (B3-13).

// ★ Build C (D3b) — VALUE THE MODEL'S IS SKELETON from the bridge's Step-4 numbers.
//
// Leaves come from the bridge's per-leaf map; plain section totals are summed from the row's own
// `sumOf` (the model's definition of what belongs to each total, including the per-department
// sub-totals it emits); and the derived rows — Gross Profit, the margins, Total SG&A, Operating
// Income, Total OIE, Net Income, EBITDA — are taken from the bridge's own arithmetic, because those
// carry sign conventions this screen owns (the OIE income-line flip in particular, where a plain sum
// of the DISPLAYED values would not equal Total OIE).
export { assembleISSkeleton };

export function evaluateISSkeleton(isLines, valuesById, named, months) {
    const R = {}; const missing = []; const seen = new Set();
    for (const row of isLines || []) {
        if (row.type === 'header') continue;
        if (named && row.id in named) { R[row.id] = named[row.id] || {}; continue; }
        if (row.type === 'total') {
            R[row.id] = {};
            for (const m of months) {
                let t = 0;
                for (const c of (row.sumOf || [])) t = r2(t + (R[c]?.[m] || 0));
                R[row.id][m] = r2(t);
            }
            continue;
        }
        if (row.type === 'calc' || row.type === 'margin') { R[row.id] = {}; missing.push(row.id); continue; }
        const v = valuesById[row.id];
        if (!v) { R[row.id] = Object.fromEntries(months.map(m => [m, 0])); missing.push(row.id); continue; }
        seen.add(row.id);
        R[row.id] = Object.fromEntries(months.map(m => [m, r2(v[m] || 0)]));
    }
    const orphans = Object.keys(valuesById).filter(k => !seen.has(k));
    return { values: R, missing, orphans };
}

export function evaluateBSSkeleton(bsLines, valuesByKey, months) {
    const R = {}; const missing = []; const seen = new Set();
    for (const row of bsLines || []) {
        if (row.type === 'header' || row.type === 'spacer') continue;
        if (row.type === 'total') {
            R[row.id] = {};
            for (const m of months) {
                let s = 0;
                for (const c of (row.sumOf || [])) s = r2(s + (R[c]?.[m] || 0));
                R[row.id][m] = r2(s);
            }
            continue;
        }
        if (row.type === 'check') { R[row.id] = valuesByKey[CHECK_ROW_ID] || {}; seen.add(CHECK_ROW_ID); continue; }
        // a leaf
        const v = valuesByKey[row.id];
        if (!v) { missing.push(row.id); R[row.id] = Object.fromEntries(months.map(m => [m, 0])); continue; }
        seen.add(row.id);
        R[row.id] = Object.fromEntries(months.map(m => [m, r2(v[m] || 0)]));
    }
    const orphans = Object.keys(valuesByKey).filter(k => !seen.has(k));
    return { values: R, missing, orphans };
}

// ★ Build A v2 — the CASH FORMULAS ARE THE MODEL'S, DERIVED HERE, NOT SUPPLIED BY THE BRIDGE.
//
// v1 handed the bridge's own precomputed cash_avail / chg_cash / end_cash straight through and
// suppressed fa_writeoff. That was measured wrong (diffs/fa-writeoff.txt): the bridge's investing
// carries a disposal's gross-FA drop as a +4,074.18 inflow exactly as the model's does, and the
// bridge had NO offsetting row — so its Ending Cash was OVERSTATED by 4,074.18 in 2021-12, the one
// month it also failed to equal the actual balance-sheet cash.
//
// ★ THE ROW AND THE TOTALS MUST MOVE TOGETHER. Deriving the write-off while still overriding the
// totals is the one genuinely broken state: a visible offset row that Ending Cash ignores, i.e. a
// cash-flow statement that does not add up. So the four cash rows are now derived exactly as
// computeCFS does (:135-139 and :193-198), and the ONLY thing the caller supplies is the opening
// seed — `beg_cash` is a TRUE ROLL from that seed, not a per-month re-seat from the balance sheet,
// which makes "ending cash equals the BS cash every month" an independent check rather than an
// identity that cannot fail.
export function evaluateCFSSkeleton(cfsLines, ctx, months, priorDecMonth) {
    const { bsValuesByKey = {}, isRef = {}, sweep = {}, formulas = {}, openingCash = 0 } = ctx || {};
    const R = {}; const missing = [];
    const prevOf = (m) => { const i = months.indexOf(m); return i === 0 ? priorDecMonth : months[i - 1]; };
    const bv = (k, m) => r2(bsValuesByKey[k]?.[m] || 0);
    for (const row of cfsLines || []) {
        if (row.type === 'header' || row.type === 'spacer') continue;
        R[row.id] = {};
        for (const m of months) {
            const pm = prevOf(m);
            // ★ AN EXPLICITLY SUPPLIED ROW WINS OVER ANY DERIVATION. The caller passing a series for
            // a row id is a statement that the bridge owns that number — used for the cash formulas,
            // and for fa_writeoff, where the model's derivation would double-count against the
            // bridge's own investing arithmetic (see the call site).
            if (row.id in formulas) { R[row.id][m] = r2(formulas[row.id][m] || 0); continue; }
            if (row.source === 'is') {
                R[row.id][m] = r2(isRef[row.isKey]?.[m] || 0);
            } else if (row.source === 'bs') {
                const d = r2(bv(row.bsKey, m) - bv(row.bsKey, pm));
                R[row.id][m] = row.sign === 'a' ? r2(-d) : d;
            } else if (row.source === 'fa_writeoff') {
                let drop = 0;
                for (const k of (row.gross_keys || [])) { const dd = r2(bv(k, pm) - bv(k, m)); if (dd > 0) drop = r2(drop + dd); }
                R[row.id][m] = r2(-drop);
            } else if (row.source === 'sweep') {
                R[row.id][m] = r2(sweep[m] || 0);
            } else if (row.sumOf) {
                let s = 0;
                for (const c of row.sumOf) s = r2(s + (R[c]?.[m] || 0));
                R[row.id][m] = r2(s);
            } else if (!CASH_FORMULA_IDS.has(row.id)) {
                R[row.id][m] = 0;
                if (m === months[0]) missing.push(row.id);
            }
        }
    }

    // ── the four cash rows, derived exactly as computeCFS derives them ──
    // cash_avail = Total Operating + Total Investing + Σ(financing BEFORE the sweep)   (computeCFS:135-139)
    const finIds = (cfsLines || []).filter(l => l.source === 'bs' && l.id.startsWith('fin_')).map(l => l.id);
    if (R.cash_avail) for (const m of months) {
        const fin = finIds.reduce((s, id) => r2(s + (R[id]?.[m] || 0)), 0);
        R.cash_avail[m] = r2((R.total_oper?.[m] || 0) + (R.total_invest?.[m] || 0) + fin);
    }
    // beg_cash / chg_cash / end_cash — a true roll from the opening seed             (computeCFS:193-198)
    if (R.beg_cash && R.chg_cash && R.end_cash) {
        let prevEnd = r2(openingCash);
        for (const m of months) {
            R.beg_cash[m] = prevEnd;
            R.chg_cash[m] = r2((R.total_oper?.[m] || 0) + (R.total_invest?.[m] || 0) + (R.total_fin?.[m] || 0));
            R.end_cash[m] = r2(R.beg_cash[m] + R.chg_cash[m]);
            prevEnd = R.end_cash[m];
        }
    }
    return { values: R, missing };
}

// ── Main entrypoint ───────────────────────────────────────────
// cashRollup (optional, ★ Build 1 PART 2): { label, memberGlNames[] } — the operator's Step-4a
// "Total Cash" rollup, rendered as a sub-total inside Current Assets. Omit it and the BS renders
// exactly as before.
export function computeHistoricalFinancials(glLines, mappings, months, priorDecMonth, sweepGlName, cashRollup, bsKeyOf, deptMappings) {
    const bsMonths = priorDecMonth && !months.includes(priorDecMonth) ? [priorDecMonth, ...months] : [...months];

    // Mapping lookup
    const mappingLookup = new Map();
    for (const m of mappings || []) {
        const key = `${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`;
        mappingLookup.set(key, m);
    }

    // Group and attach mappings
    const grouped = groupLines(glLines || [], months, bsMonths).map(l => attachMapping(l, mappingLookup));

    // W-082: the revenue-key rule the Step-4 import uses, over the same raw lines and mappings.
    const resolveRevKey = buildRevKeyResolver(glLines || [], mappings || []);
    const isData = computeIS(grouped, months, deptMappings, resolveRevKey);
    const bsData = computeBS(grouped, bsMonths, isData.netIncome, cashRollup, bsKeyOf);
    const cfsData = computeCFS(isData, bsData, months, priorDecMonth, sweepGlName);

    // Strip private fields before returning
    const { _buckets, ...bsPublic } = bsData;

    return { is: isData, bs: bsPublic, cfs: cfsData };
}

export default computeHistoricalFinancials;
